import { useGetAllProvincesQuery, useGetExamEventByIdQuery } from '@exam-center/graphql';
import { Navigate } from '@exam-center/router';
import { Loading } from '@shared/components';
import { createContext, useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import type { ExamEventDetailsContextValue, ExamEventDetailsProviderProps } from './types';

const ExamEventDetailsContext = createContext<ExamEventDetailsContextValue | undefined>(undefined);

export default function ExamEventDetailsProvider(props: Readonly<ExamEventDetailsProviderProps>) {
  const { examCenterCode, examEventId, children } = props;

  const { data: getExamEventByIdResult, loading: isUseGetExamEventByIdLoading } =
    useGetExamEventByIdQuery({
      variables: {
        getExamEventByIdInput: {
          id: examEventId,
        },
      },
    });

  const examEvent = getExamEventByIdResult?.ecGetExamEventById?.examEvent;

  const { data: getProvincesResult } = useGetAllProvincesQuery({
    variables: {},
  });

  const provinces = useMemo(
    () => getProvincesResult?.ecGetAllProvinces?.provinces ?? [],
    [getProvincesResult],
  );
  const contextValue: ExamEventDetailsContextValue = useMemo(() => {
    return {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      examEvent: examEvent!,
      provinces: provinces,
    };
  }, [examEvent, provinces]);

  if (isUseGetExamEventByIdLoading) {
    return <Loading sx={{ height: '100dvh' }} />;
  }

  if (!examEvent) {
    return (
      <Navigate
        to='/:examCenterCode/exam-events'
        params={{
          examCenterCode,
        }}
      />
    );
  }

  return (
    <ExamEventDetailsContext.Provider value={contextValue}>
      {children ?? <Outlet />}
    </ExamEventDetailsContext.Provider>
  );
}

export { ExamEventDetailsContext };
